import * as React from 'react';
import {useEffect, useState} from 'react';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import userService from '../../services/UserService';
import {Accordion, AccordionDetails, AccordionSummary, Box, Button, Checkbox, Chip, FormControl, FormControlLabel, Grid2 as Grid, InputLabel, MenuItem, OutlinedInput, Select, TextField, Tooltip} from '@mui/material';
import {Add, ArrowDropUp, EventAvailable, FilterAltOff, KeyboardArrowDown} from '@mui/icons-material';
import {useNavigate} from 'react-router-dom';
import {useTitle} from '../dashboard/TitleContext';
import {useSnackbar} from "../../util/SnackbarProvider";
import {ROLES, SALARY_TEMPLATES} from "../../util/Const";
import IconButton from "@mui/material/IconButton";
import {ArrowDropDownIcon} from "@mui/x-date-pickers";
import FilterPanel from "../common/FilterPanel";

const ALL_COLUMNS = [
    {id: 'name', label: 'Név'},
    {id: 'email', label: 'Email'},
    {id: 'role', label: 'Szerepkör'},
    {id: 'availableDayOffs', label: 'Elérhető Szabadnapok', align: 'center'},
    {id: 'salaryTemplate', label: 'Fizetési Kategória', f: t => SALARY_TEMPLATES[t]},
    {id: 'registrationDate', label: 'Regisztráció'}
];

const DEFAULT_COLUMNS = ['name', 'email', 'role'];

const LOCAL_STORAGE_ITEM_NAME = "user-rows-per-page"
const LOCAL_STORAGE_SEARCH_PARAMS = "user-search-params"
const LOCAL_STORAGE_COLUMN_NAMES = "user-column-names"
const DEFAULT_SEARCH_PARAMS = {}

export default function UserTable() {

    useTitle([{name: 'Felhasználók'}])

    const [page, setPage] = React.useState(0);
    const [rows, setRows] = React.useState([]);
    const [rowsPerPage, setRowsPerPage] = React.useState(localStorage.getItem(LOCAL_STORAGE_ITEM_NAME) || 24);
    const [searchParams, setSearchParams] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_SEARCH_PARAMS)) || {})
    const [columnNames, setColumnNames] = React.useState(JSON.parse(localStorage.getItem(LOCAL_STORAGE_COLUMN_NAMES)) || DEFAULT_COLUMNS)
    const [totalCount, setTotalCount] = React.useState(0);
    const {sendAlertMessage} = useSnackbar()
    const [expanded, setExpanded] = useState(false);
    const filterPanelRef = React.useRef();

    useEffect(() => {
        userService.search(buildQueryParams())
            .then(response => {
                const result = response.content.map((user) => {
                    const role = ROLES[user.role] || "ERROR";
                    return {...user, role: role}
                })
                setTotalCount(response.totalElements)
                setRows(result);
            })
            .catch(error => sendAlertMessage(error?.message || "Felhasználók lekérése sikertelen", 'error'))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [page, rowsPerPage, searchParams]);

    function buildQueryParams() {
        return {
            page: page,
            size: rowsPerPage,
            ...(searchParams.name && {name: searchParams.name}),
            ...(searchParams.email && {email: searchParams.email}),
            ...(searchParams.roles && {roles: searchParams.roles}),
            ...(searchParams.salaryTemplates && {salaryTemplates: searchParams.salaryTemplates}),
            ...(searchParams.orderBy && {orderBy: searchParams.orderBy}),
            ...(searchParams.sort && {sort: searchParams.sort}),
        }
    }

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        const rowsPerPage = event.target.value
        setRowsPerPage(event.target.value);
        localStorage.setItem(LOCAL_STORAGE_ITEM_NAME, rowsPerPage)
        setPage(0);
    };

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
    }, [searchParams]);

    const navigate = useNavigate();

    const onSearchParamsChange = (event, n, v) => {
        let name = event?.target?.name || n
        let value = event?.target?.value || v
        setSearchParams((prev) => ({
            ...prev,
            [name]: value
        }))
    }

    const clear = () => {
        if (searchParams != null) sendAlertMessage('Szűrők alaphelyzetbe állítva', 'info')
        setSearchParams(DEFAULT_SEARCH_PARAMS)
        filterPanelRef.current?.resetSelection();
    }

    useEffect(() => {
        if (searchParams) localStorage.setItem(LOCAL_STORAGE_SEARCH_PARAMS, JSON.stringify(searchParams));
        else localStorage.removeItem(LOCAL_STORAGE_SEARCH_PARAMS)
    }, [searchParams]);

    const onMultipleSelectChange = (event) => {
        const {value, name} = event.target
        const newSelection = typeof value === 'string' ? value.split(',') : value;
        const uniqueSelection = newSelection.reduce((acc, object) => {
            const exists = acc.find(item => item === object);
            if (exists) return acc.filter(item => item !== object);
            else return [...acc, object];
        }, []);

        setSearchParams((prev) => ({
            ...prev,
            [name]: uniqueSelection,
        }));
    };

    const MenuProps = {
        PaperProps: {
            style: {
                maxHeight: 48 * 4.5 + 8,
                width: 250
            },
        },
    };

    function getStyles(name, statuses) {
        if (statuses == null || statuses.indexOf(name) === -1) return
        return {
            backgroundColor: "#2e7d32",
            color: "white"
        };
    }

    const onChangeCheckbox = (event) => {
        const { checked, name } = event.target;
        setColumnNames((prev) => {
            return checked ? prev.includes(name) ? prev : [...prev, name] : prev.filter((col) => col !== name);
        });
    };


    return (
        <>
            <Accordion style={{marginBottom: "10px"}} disableGutters expanded={expanded}
                       onChange={() => setExpanded(!expanded)}>
                <AccordionSummary
                    expandIcon={<KeyboardArrowDown sx={{marginRight: "0px", height: "2em", width: "auto"}}/>} sx={{
                    minHeight: "0px",
                    fontSize: "24px",
                    fontWeight: "bold",
                    backgroundColor: "darkgray",
                    borderRadius: "4px 4px 0px 0px",
                    paddingLeft: "20px",
                    margin: "0"
                }}>
                    Beállítások
                </AccordionSummary>
                <AccordionDetails className="filter-panel">
                    <Grid container columns={10} spacing={2} padding={"10px"}>
                        <Grid size={{xs: 7}}>
                            <Grid container columns={20} spacing={2} padding={"10px"}>
                                {ALL_COLUMNS.map((item, index) => (
                                    <FormControlLabel
                                        control={<Checkbox
                                            variant="outlined"
                                            margin="normal"
                                            size="large"
                                            name={item.id}
                                            onChange={onChangeCheckbox}
                                            checked={columnNames?.includes(item.id) || false}
                                        />} label={item.label}
                                        style={{paddingTop: "20px"}}/>
                                ))}
                            </Grid>
                        </Grid>
                        <Grid size={{xs: 3}}>
                            <FilterPanel
                                type="USER"
                                ref={filterPanelRef}
                                currentSearchParams={searchParams}
                                onFilterSelect={(params) => setSearchParams(params)}
                            />
                        </Grid>
                    </Grid>
                </AccordionDetails>
            </Accordion>
            <Paper sx={{width: '100%', overflow: 'hidden', marginBottom: "30px"}}>
                <TableContainer>
                    <Table stickyHeader aria-label="sticky table" sx={{tableLayout: "fixed"}}>
                        <TableHead>
                            <TableRow>
                                <TableCell key="actions"
                                           style={{width: "100px"}}
                                           align='right' padding='none'>
                                    <Tooltip title="Új hozzáadása">
                                        <Button variant='contained' color='success'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={() => navigate("/dashboard/users/register")}>
                                            <Add fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                    <Tooltip title="Szűrők törlése">
                                        <Button variant='contained' color='info'
                                                sx={{minWidth: "0px", padding: "0px", marginRight: "10px"}}
                                                onClick={clear}>
                                            <FilterAltOff fontSize="large"/>
                                        </Button>
                                    </Tooltip>
                                </TableCell>
                                {columnNames.includes('name') && <TableCell sx={{padding: "8px"}}>
                                    <TextField name="name" label="Név" size="small" value={searchParams.name || ""} onChange={onSearchParamsChange} sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                    <IconButton onClick={() => setSearchParams(prev => ({...prev, orderBy: 'name', sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'}))}>
                                        {searchParams.sort == null || searchParams.orderBy !== 'name' ? <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                            searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                    </IconButton>
                                </TableCell>}
                                {columnNames.includes('email') && <TableCell sx={{padding: "8px"}}>
                                    <TextField name="email" label="E-mail" size="small" value={searchParams.email} onChange={onSearchParamsChange} sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off"/>
                                    <IconButton onClick={() => setSearchParams(prev => ({
                                        ...prev,
                                        orderBy: 'email',
                                        sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
                                    }))}>                                        {searchParams.sort == null || searchParams.orderBy !== 'email' ? <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                        searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                    </IconButton>
                                </TableCell>}
                                {columnNames.includes('role') && <TableCell sx={{padding: "8px", minWidth: "200px"}}>
                                    <FormControl size="small" sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off">
                                        <InputLabel id="roles-label">Szerepkör</InputLabel>
                                        <Select
                                            labelId="roles-label"
                                            multiple
                                            value={searchParams.roles || []}
                                            onChange={onMultipleSelectChange}
                                            name="roles"
                                            variant="outlined"
                                            input={<OutlinedInput id="select-multiple-chip" label="Prioritás"/>}
                                            renderValue={(selected) => (
                                                <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: 1.0, margin: "0px"}}>
                                                    {selected.map((value, index) => (
                                                        <Chip key={index} label={ROLES[value]} sx={{fontSize: "12px", height: "20px", "& span": {padding: "5px"}}}/>
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{}}
                                            MenuProps={MenuProps}
                                        >
                                            {Object.entries(ROLES).map(([key, value]) => (
                                                <MenuItem key={key} value={key} style={getStyles(key, searchParams.roles)}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <IconButton onClick={() => setSearchParams(prev => ({
                                        ...prev,
                                        orderBy: 'role',
                                        sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
                                    }))}>                                        {searchParams.sort == null || searchParams.orderBy !== 'role' ? <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                        searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                    </IconButton>
                                </TableCell>}
                                {columnNames.includes('availableDayOffs') && <TableCell sx={{padding: "0px", textAlign: "center"}}>
                                    <Tooltip title="Elérhető Szabadnapok"><EventAvailable sx={{ color: "gray", fontSize: "40px", marginTop: "8px"}}/></Tooltip>
                                </TableCell>}
                                {columnNames.includes('salaryTemplate') && <TableCell sx={{padding: "8px", minWidth: "260px"}}>
                                    <FormControl size="small" sx={{margin: "0px", width: "calc(100% - 40px)"}} autoComplete="off">
                                        <InputLabel id="salary-templates-label">Fizetési Kategória</InputLabel>
                                        <Select
                                            labelId="salary-templates-label"
                                            multiple
                                            value={searchParams.salaryTemplates || []}
                                            onChange={onMultipleSelectChange}
                                            name="salaryTemplates"
                                            variant="outlined"
                                            input={<OutlinedInput id="select-multiple-chip" label="Fizetési Kategória"/>}
                                            renderValue={(selected) => (
                                                <Box sx={{display: 'flex', flexWrap: 'nowrap', gap: 1.0, margin: "0px"}}>
                                                    {selected.map((value, index) => (
                                                        <Chip key={index} label={SALARY_TEMPLATES[value]} sx={{fontSize: "12px", height: "20px", "& span": {padding: "5px"}}}/>
                                                    ))}
                                                </Box>
                                            )}
                                            sx={{}}
                                            MenuProps={MenuProps}
                                        >
                                            {Object.entries(SALARY_TEMPLATES).map(([key, value]) => (
                                                <MenuItem key={key} value={key} style={getStyles(key, searchParams.salaryTemplates)}>
                                                    {value}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                    <IconButton onClick={() => setSearchParams(prev => ({
                                        ...prev,
                                        orderBy: 'salaryTemplate',
                                        sort: searchParams.sort === 'ASC' ? 'DESC' : 'ASC'
                                    }))}>                                        {searchParams.sort == null || searchParams.orderBy !== 'salaryTemplate' ? <ArrowDropDownIcon sx={{color: "lightgray"}}/> :
                                        searchParams.sort === 'ASC' ? <ArrowDropDownIcon/> : <ArrowDropUp/>}
                                    </IconButton>
                                </TableCell>}
                                {columnNames.includes('registrationDate') && <TableCell>Regisztráció</TableCell>}
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {rows && rows.map((row) => {
                                return (
                                    <TableRow hover role="checkbox" tabIndex={-1} key={row.id} sx={{background: !row.active ? "#ddd" : ""}}>
                                        <TableCell key="actions" padding='none' align='right'></TableCell>
                                        {ALL_COLUMNS.filter(c => columnNames.includes(c.id)).map((column) => {
                                            const value = column.f ? column.f(row[column.id]) : row[column.id];
                                            return (
                                                <TableCell key={column.id} align={column.align}
                                                           onClick={() => navigate("/dashboard/users/" + row.id)}
                                                           sx={{cursor: "pointer", padding: "10px", color: !row.active ? "gray" : ""}}>
                                                    {value}
                                                </TableCell>
                                            );
                                        })}
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25, 100]}
                    component="div"
                    count={totalCount}
                    rowsPerPage={parseInt(rowsPerPage)}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    labelRowsPerPage="Elemek oldalanként:"
                />
            </Paper>
        </>
    );
}
